<template>
  <button
    class="add-card-toolbar-icon px-1 flex items-center justify-center"
    @click="toggleSwatches"
  >
    <em
      class="custom-icons page-toolbar group-hover:bg-icon-hover"
      :style="getCustomIconStyle(iconPath('color'))"
    />
  </button>
</template>

<script setup lang="ts">
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { inject } from 'vue';
import ColorButton from './ColorButton.vue';
import { MenuItemFn } from '../menu-item';

defineProps<{
  effect: (color: string) => () => void;
}>();

inject('editor');

const { overrideCommands, revertCommandOverrides } = inject<{
  overrideCommands: (com: MenuItemFn[]) => void;
  revertCommandOverrides: () => void;
}>('menuCommandOverride')!;

const { iconPath, getCustomIconStyle } = useCustomIcon();

function makeColor(color: string, props: Parameters<MenuItemFn>[0]) {
  (makeColor as any).index = (makeColor as any).index ?? 0;
  return {
    icon: 'circle',
    key: (makeColor as any).index++,
    effect() {
      (props.editor.chain().focus() as any).setCellAttribute('stTableColor', color).run();
      revertCommandOverrides();
    },
    customProps: {
      color,
    },
    customButton: ColorButton as any,
  };
}

function toggleSwatches() {
  overrideCommands([
    ({ editor, translate, store }) => [
      {
        icon: 'arrow-left',
        effect: () => {
          revertCommandOverrides();
        },
      },
      makeColor('primary', { editor, translate, store }),
      makeColor('error', { editor, translate, store }),
      makeColor('warning', { editor, translate, store }),
      makeColor('success', { editor, translate, store }),
      makeColor('bluelight', { editor, translate, store }),
      makeColor('indigo', { editor, translate, store }),
      makeColor('purple', { editor, translate, store }),
      makeColor('pink', { editor, translate, store }),
      makeColor('orange', { editor, translate, store }),
      makeColor('gray', { editor, translate, store }),
      {
        icon: 'x',
        tooltip: translate('menu.clear-table-cell-color'),
        effect: () => {
          (editor.chain().focus() as any)
            .setCellAttribute('stTableColor', null)
            .setCellAttribute('backgroundColor', null)
            .run();
          revertCommandOverrides();
        },
      },
    ],
  ]);
}
</script>

<style lang="scss">
td[style]:not([data-st-table-color]) {
  & > * {
    color: #e4e7ec;
    text-shadow: 0 0 2px #101828;
  }
}

td[data-st-table-color='primary'],
em[data-st-table-color='primary'] {
  background-color: #e5f3ff !important;
  body[data-theme='dark'] & {
    background-color: #293056 !important;
  }
}
td[data-st-table-color='error'],
em[data-st-table-color='error'] {
  background-color: #fee4e2 !important;
  body[data-theme='dark'] & {
    background-color: #7a271a !important;
  }
}
td[data-st-table-color='warning'],
em[data-st-table-color='warning'] {
  background-color: #fef0c7 !important;
  body[data-theme='dark'] & {
    background-color: #7a2e0e !important;
  }
}
td[data-st-table-color='success'],
em[data-st-table-color='success'] {
  background-color: #d1fadf !important;
  body[data-theme='dark'] & {
    background-color: #054f31 !important;
  }
}
td[data-st-table-color='bluelight'],
em[data-st-table-color='bluelight'] {
  background-color: #e0f2fe !important;
  body[data-theme='dark'] & {
    background-color: #0b4a6f !important;
  }
}
td[data-st-table-color='indigo'],
em[data-st-table-color='indigo'] {
  background-color: #e0eaff !important;
  body[data-theme='dark'] & {
    background-color: #2d3282 !important;
  }
}
td[data-st-table-color='purple'],
em[data-st-table-color='purple'] {
  background-color: #ebe9fe !important;
  body[data-theme='dark'] & {
    background-color: #3e1c96 !important;
  }
}
td[data-st-table-color='pink'],
em[data-st-table-color='pink'] {
  background-color: #fce7f6 !important;
  body[data-theme='dark'] & {
    background-color: #851651 !important;
  }
}
td[data-st-table-color='orange'],
em[data-st-table-color='orange'] {
  background-color: #ffead5 !important;
  body[data-theme='dark'] & {
    background-color: #7e2410 !important;
  }
}
td[data-st-table-color='gray'],
em[data-st-table-color='gray'] {
  background-color: #e4e7ec !important;
  body[data-theme='dark'] & {
    background-color: #344054 !important;
  }
}
</style>
